import * as React from 'react';
import { ContentLoading, PermissionGroup, getViewPermission } from '@vcd-ecm-ui/Common';
import { SiteNavLink, LeftNav as CommonLeftNav } from '@vcd-ecm-ui/LeftNav';
import { Redirect, useLocation } from 'react-router-dom';
import { LINKS } from './nav-links';
import useUser from '../../hooks/user/useUser';

// Create map variable with left nav links to the actual permission group
const leftNavPermissions = new Map<string, PermissionGroup>([
  ['/certificates', PermissionGroup.Certificates],
  ['/certificates-old', PermissionGroup.Certificates],
  ['/customers', PermissionGroup.Customers],
  ['/customers-old', PermissionGroup.Customers],
  ['/settings', PermissionGroup.Settings],
  ['/settings/certificate/1', PermissionGroup.Settings],
  ['/settings/customer/1', PermissionGroup.Settings],
  ['/settings/users', PermissionGroup.Settings],
  ['/settings/taxpayers', PermissionGroup.Settings],
  ['/ecwizard', PermissionGroup.ECWizard],
  ['/ecwizard/settings', PermissionGroup.ECWizard],
  ['/ecwizard/sellers', PermissionGroup.ECWizard],
  ['/ecwizard/jurisdictions', PermissionGroup.ECWizard],
  ['/ecwizard/products', PermissionGroup.ECWizard],
  ['/ecwizard/exemption-types', PermissionGroup.ECWizard],
  ['/reports', PermissionGroup.Reports],
  ['/reports/certificates', PermissionGroup.Reports],
  ['/reports/audits', PermissionGroup.Reports],
  ['/reports/customs', PermissionGroup.Reports],
  ['/reports/activity-log', PermissionGroup.Reports],
  ['/reports/error-log', PermissionGroup.Reports],
  ['/correspondence', PermissionGroup.Correspondence], // Parent permission
  ['/correspondence/templates', PermissionGroup.Correspondence],
  ['/correspondence/subscriptions', PermissionGroup.Correspondence],
  ['/correspondence/configuration/status', PermissionGroup.CorrespondenceConfiguration], // Child Permission
]);

const LeftNav = (): JSX.Element => {
  const [redirectTo, setRedirectTo] = React.useState(null);
  const [activeLink, setActiveLink] = React.useState(null);
  const location = useLocation();
  const [userInfo] = useUser();

  const openInNewTab = (url: string): void => {
    const newWindow: Window = window.open(url, '_blank');
    newWindow.focus();
  };

  const isLinkActive = (link: SiteNavLink): boolean => {
    if (link && activeLink) {
      if (link.value === activeLink) {
        return true;
      }

      if (link.otherValues) {
        let otherValueActive = false;

        link.otherValues.forEach((otherValue: string) => {
          if (activeLink.includes(otherValue)) {
            otherValueActive = true;
          }
        });

        return otherValueActive;
      }
    }

    return false;
  };

  const getPermissionSubLinks = (link: SiteNavLink): SiteNavLink[] => {
    // Filter the left nav sub links which are having view permissions and render only those links.
    // Check of Child Permission first.  If it does not exist use Parent Permission.
    // eslint-disable-next-line max-len
    const permissionSubLinks = link.subLinks.filter((sublink:SiteNavLink) => getViewPermission(userInfo, leftNavPermissions.get(sublink.value as string) || leftNavPermissions.get(link.value as string)));

    return permissionSubLinks;
  };

  // Update the Active Link when the Location Pathname changes
  React.useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  // Filter the left nav links which are having view permissions and render only those links
  const permissionLinks = LINKS.filter((link:SiteNavLink) => getViewPermission(userInfo, leftNavPermissions.get(link.value as string)));

  const links = permissionLinks.map(
    (link): SiteNavLink => ({
      ...link,
      isActive: isLinkActive(link),
      isRoot: true,
      onClick: (): void => {
        if (link.isExternal) {
          openInNewTab(link.value.toString());
        } else {
          setRedirectTo(link.value);
          setActiveLink(link.value);
        }
      },
      // Render the Sub Links of the Link if any
      subLinks: !link.subLinks
        ? null
        : getPermissionSubLinks(link).map(
          (sublink): SiteNavLink => ({
            ...sublink,
            isActive: isLinkActive(sublink),
            isRoot: false,
            onClick: (): void => {
              if (sublink.isExternal) {
                openInNewTab(sublink.value.toString());
              } else {
                setRedirectTo(sublink.value);
                setActiveLink(sublink.value);
              }
            },
          }),
        ),
    }),
  );

  if (redirectTo) {
    setTimeout(() => setRedirectTo(''), 0);
    return <Redirect to={redirectTo} />;
  }

  return <CommonLeftNav links={links} />;
};

export default LeftNav;
